import { securityConfigSchema } from '../domain/schemas';
import type { SecurityConfig } from '../domain/types';
import { db, initializeDatabase, type WaschPilotDatabase } from '../data/database';

export const PIN_DERIVATION_ITERATIONS = 600_000;
const SALT_LENGTH_BYTES = 16;
const HASH_LENGTH_BITS = 256;

export class PinValidationError extends Error {
  constructor(message = 'PIN must contain 6 to 12 digits.') {
    super(message);
    this.name = 'PinValidationError';
  }
}

const PIN_DIGITS: Readonly<Record<string, string>> = {
  '٠': '0',
  '١': '1',
  '٢': '2',
  '٣': '3',
  '٤': '4',
  '٥': '5',
  '٦': '6',
  '٧': '7',
  '٨': '8',
  '٩': '9',
  '۰': '0',
  '۱': '1',
  '۲': '2',
  '۳': '3',
  '۴': '4',
  '۵': '5',
  '۶': '6',
  '۷': '7',
  '۸': '8',
  '۹': '9',
};

export function normalizePin(pin: string): string {
  return [...pin.trim()].map((character) => PIN_DIGITS[character] ?? character).join('');
}

export function validatePin(pin: string): string {
  const normalized = normalizePin(pin);
  if (!/^\d{6,12}$/.test(normalized)) throw new PinValidationError();
  return normalized;
}

function getCrypto(): Crypto {
  const webCrypto = globalThis.crypto;
  if (!webCrypto?.subtle) throw new Error('Web Crypto API is unavailable.');
  return webCrypto;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

async function derivePinBytes(
  pin: string,
  salt: Uint8Array,
  iterations: number,
): Promise<Uint8Array> {
  const webCrypto = getCrypto();
  const encodedPin = new TextEncoder().encode(pin);
  const pinBuffer = new Uint8Array(encodedPin).buffer;
  const saltBuffer = new Uint8Array(salt).buffer;
  const keyMaterial = await webCrypto.subtle.importKey('raw', pinBuffer, 'PBKDF2', false, [
    'deriveBits',
  ]);
  const derived = await webCrypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: saltBuffer, iterations },
    keyMaterial,
    HASH_LENGTH_BITS,
  );
  return new Uint8Array(derived);
}

function constantTimeEqual(left: Uint8Array, right: Uint8Array): boolean {
  if (left.length !== right.length) return false;
  let difference = 0;
  for (let index = 0; index < left.length; index += 1) {
    difference |= (left[index] ?? 0) ^ (right[index] ?? 0);
  }
  return difference === 0;
}

export async function createPinConfig(
  pin: string,
  options: { iterations?: number; now?: string } = {},
): Promise<SecurityConfig> {
  const normalizedPin = validatePin(pin);
  const iterations = options.iterations ?? PIN_DERIVATION_ITERATIONS;
  if (!Number.isInteger(iterations) || iterations < 100_000 || iterations > 2_000_000) {
    throw new RangeError('PBKDF2 iterations must be between 100000 and 2000000.');
  }

  const salt = getCrypto().getRandomValues(new Uint8Array(SALT_LENGTH_BYTES));
  const derivedHash = await derivePinBytes(normalizedPin, salt, iterations);
  const now = options.now ?? new Date().toISOString();
  return securityConfigSchema.parse({
    id: 'teacher-pin',
    algorithm: 'PBKDF2-SHA-256',
    algorithmVersion: 1,
    iterations,
    salt: bytesToBase64(salt),
    derivedHash: bytesToBase64(derivedHash),
    createdAt: now,
    updatedAt: now,
  });
}

export async function verifyPinAgainstConfig(
  pin: string,
  configuration: SecurityConfig,
): Promise<boolean> {
  let normalizedPin: string;
  try {
    normalizedPin = validatePin(pin);
  } catch {
    return false;
  }

  const parsed = securityConfigSchema.parse(configuration);
  const actual = await derivePinBytes(normalizedPin, base64ToBytes(parsed.salt), parsed.iterations);
  return constantTimeEqual(actual, base64ToBytes(parsed.derivedHash));
}

export async function hasTeacherPin(database: WaschPilotDatabase = db): Promise<boolean> {
  await initializeDatabase(database);
  return (await database.security.get('teacher-pin')) !== undefined;
}

export async function setTeacherPin(pin: string, database: WaschPilotDatabase = db): Promise<void> {
  // Web Crypto promises are not Dexie-aware; derive before opening the short write transaction.
  const configuration = await createPinConfig(pin);
  await initializeDatabase(database);
  await database.transaction('rw', database.security, async () => {
    if (await database.security.get('teacher-pin')) {
      throw new Error('A teacher PIN already exists.');
    }
    await database.security.add(configuration);
  });
}

export async function verifyTeacherPin(
  pin: string,
  database: WaschPilotDatabase = db,
): Promise<boolean> {
  await initializeDatabase(database);
  const configuration = await database.security.get('teacher-pin');
  return configuration ? verifyPinAgainstConfig(pin, configuration) : false;
}

export async function changeTeacherPin(
  currentPin: string,
  newPin: string,
  database: WaschPilotDatabase = db,
): Promise<boolean> {
  await initializeDatabase(database);
  const current = await database.security.get('teacher-pin');
  if (!current || !(await verifyPinAgainstConfig(currentPin, current))) return false;
  const next = await createPinConfig(newPin);
  next.createdAt = current.createdAt;
  await database.security.put(securityConfigSchema.parse(next));
  return true;
}

/** Used only by the strongly-confirmed factory-reset workflow. */
export async function removeTeacherPin(database: WaschPilotDatabase = db): Promise<void> {
  await initializeDatabase(database);
  await database.security.delete('teacher-pin');
}

export class TeacherPinSession {
  private unlockedUntil = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private autoLockMinutes = 10,
    private readonly onLock?: () => void,
  ) {
    this.setAutoLockMinutes(autoLockMinutes);
  }

  get isUnlocked(): boolean {
    if (this.unlockedUntil <= Date.now()) {
      if (this.unlockedUntil !== 0) this.lock();
      return false;
    }
    return true;
  }

  async unlock(pin: string, database: WaschPilotDatabase = db): Promise<boolean> {
    const valid = await verifyTeacherPin(pin, database);
    if (valid) this.touch();
    return valid;
  }

  touch(): void {
    this.unlockedUntil = Date.now() + this.autoLockMinutes * 60_000;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.lock(), this.autoLockMinutes * 60_000);
  }

  lock(): void {
    const wasUnlocked = this.unlockedUntil > 0;
    this.unlockedUntil = 0;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    if (wasUnlocked) this.onLock?.();
  }

  setAutoLockMinutes(minutes: number): void {
    if (!Number.isInteger(minutes) || minutes < 1 || minutes > 240) {
      throw new RangeError('Auto-lock duration must be between 1 and 240 minutes.');
    }
    this.autoLockMinutes = minutes;
    if (this.unlockedUntil > 0) this.touch();
  }
}

export const pinService = {
  hasPin: hasTeacherPin,
  setPin: setTeacherPin,
  verifyPin: verifyTeacherPin,
  changePin: changeTeacherPin,
  removePin: removeTeacherPin,
};
